import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {useNavigate, useParams} from 'react-router-dom'
import Calendar from 'react-calendar'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import {
  PTLogoWhite,
  PTKeapWhite,
  PTCalendarDaySVG,
  PTClockSVG,
  PTCalendarCallSVG,
  PTArrowLeftSVG,
  PTArrowRightSVG,
} from '../../../../assets'
import {useProposalAuthClient} from '../ClientProposalFlow'
import {useEnsureProposalInfo} from '../../../../utils/client-proposal.util'
import {
  hasStarterOnboardingOptIn,
  buildProductGroupCodes,
} from '../../../../utils/scheduling-call-rules'
import {
  selectRecurlyAccountInfo,
  selectSelectedOptionId,
  selectOriginalOptions,
  setRecurlyAccountInfo,
  updateProposalInfo,
} from '../../../../app-store/ProposalSlice'
import {validateEmailWithApi} from '../../../../accountManagement/features/EmailLookup/EmailLookup.utils'
import {Button} from '../../../../core/shared/Button'
import {
  PageWrap,
  Container,
  NavBar,
  NavLeft,
  NavRight,
  NavForLabel,
  NavBusinessName,
  GradientSection,
  HeroArea,
  PageTitle,
  PageSubtitle,
  CardOuter,
  ScheduleCard,
  CardBody,
  SpanishRow,
  SpanishLabel,
  ToggleGroup,
  ToggleBtn,
  ScheduleColumns,
  ScheduleColumn,
  ColumnLabel,
  CalendarWrap,
  SlotGrid,
  SlotBtn,
  SlotsEmpty,
  SlotsLoading,
  TimeZoneTag,
  ScheduledBadge,
  ActionBar,
  ActionLeft,
  ActionRight,
  PageFooter,
  FooterText,
  CardsSection,
  RegionRow,
  RegionLabel,
  RegionSelect,
} from './styled.components'
// import {useFormatSubscriptions} from '../ClientProposalFlow/hooks/use-format-subscriptions'

dayjs.extend(utc)
dayjs.extend(timezone)

const CALL_TYPES = [
  {
    key: 'kickoff',
    label: 'Kickoff Call',
    appointmentType: 'Onboarding',
    endpoint: 'plans/onboarding',
    productTypes: ['CENTER'],
  },
]

const ONBOARDING_REGION_OPTIONS = [
  {name: 'Coastal', value: 'Coastal'},
  {name: 'Midwest', value: 'Midwest'},
  {name: 'South', value: 'South'},
  {name: 'Inside', value: 'Inside'},
  {name: 'Inbound', value: 'Inbound'},
]

function resolveCallTypes(option) {
  if (!option?.items?.length) return [CALL_TYPES[0]]
  const hasCenters = option.items.some(i => i.productType === 'CENTER')
  return hasCenters ? [CALL_TYPES[0]] : [CALL_TYPES[0]]
}

function formatSlotTime(startTime, tzId) {
  try {
    return tzId
      ? dayjs(startTime).utc().tz(tzId).format('h:mm A')
      : dayjs(startTime).format('h:mm A')
  } catch {
    return dayjs(startTime).format('h:mm A')
  }
}

// Short label for the scheduling time zone, e.g. "America/Chicago" → "CDT".
// refDate matters: the abbreviation flips with daylight saving.
function formatTimeZoneLabel(tzId, refDate) {
  if (!tzId) return ''
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tzId,
      timeZoneName: 'short',
    }).formatToParts(refDate ? new Date(refDate) : new Date())
    return parts.find(p => p.type === 'timeZoneName')?.value || tzId
  } catch {
    return tzId
  }
}

// Initial per-tab state shape
function initTabState() {
  return {
    selectedDate: null,
    slots: [],
    selectedSlot: null,
    timeZoneId: null,
    serviceId: null,
    loading: false,
  }
}

function ClientProposalSchedule() {
  const navigate = useNavigate()
  const {proposalId} = useParams()
  const authClient = useProposalAuthClient()
  const {proposalInfo, loading: proposalLoading} = useEnsureProposalInfo({
    proposalId,
    client: authClient,
  })
  const dispatch = useDispatch()
  const selectedOptionId = useSelector(selectSelectedOptionId)
  const recurlyAccountInfo = useSelector(selectRecurlyAccountInfo)
  const originalOptions = useSelector(selectOriginalOptions)
  const abortRef = useRef(null)

  // Recovery: if Redux was reset (page reload / direct URL), re-fetch Recurly
  // address so the available-times call has a valid address param.
  useEffect(() => {
    if (proposalLoading || !proposalInfo.clientEmail) return
    if (recurlyAccountInfo?.address) return
    validateEmailWithApi({
      email: proposalInfo.clientEmail,
      client: authClient,
      flowType: 'Proposal',
    })
      .then(({meta}) => {
        if (meta?.subDetails) dispatch(setRecurlyAccountInfo(meta.subDetails))
      })
      .catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposalLoading, proposalInfo.clientEmail])
  // Use originalOptions (pre-Recurly-overwrite) so productGroupCodes come from
  // the proposal DB items, not the client's existing Recurly subscriptions.
  const selectedOption =
    (originalOptions.length
      ? originalOptions.find(o => o.optionId === selectedOptionId) ||
        originalOptions[0]
      : null) ||
    proposalInfo.options?.find(o => o.optionId === selectedOptionId) ||
    proposalInfo.options?.[0]

  // For alreadyIncluded detection we must use proposalInfo.options, not
  // originalOptions — setRecurlyAccountInfo only marks alreadyIncluded on the
  // live proposalInfo.options, never on the pre-Recurly originalOptions snapshot.
  const enrichedOption =
    proposalInfo.options?.find(o => o.optionId === selectedOptionId) ||
    proposalInfo.options?.[0]

  const callTypes = resolveCallTypes(selectedOption)

  const isThryvStarterOnboarding = useMemo(
    () => hasStarterOnboardingOptIn(selectedOption?.items ?? []),
    [selectedOption],
  )

  const activeTab = callTypes[0].key
  const [inSpanish, setInSpanish] = useState(false)
  const [selectedOnboardingRegion, setSelectedOnboardingRegion] = useState('')
  // Each tab maintains independent date / slot state so switching tabs doesn't reset selection
  const [tabState, setTabState] = useState(() =>
    Object.fromEntries(callTypes.map(ct => [ct.key, initTabState()])),
  )
  const [bookedSlots, setBookedSlots] = useState({})
  const [scheduling, setScheduling] = useState(false)
  const [scheduleError, setScheduleError] = useState(null)
  const [continuing, setContinuing] = useState(false)
  const [scheduleOffsetDetails, setScheduleOffsetDetails] = useState([])

  const activeCallType =
    callTypes.find(t => t.key === activeTab) || callTypes[0]

  // Read active tab's local state
  const curTab = tabState[activeTab] || initTabState()
  const {
    selectedDate,
    slots,
    selectedSlot,
    timeZoneId,
    serviceId,
    loading: loadingSlots,
  } = curTab

  function setTabField(tab, fields) {
    setTabState(prev => ({
      ...prev,
      [tab]: {...(prev[tab] || initTabState()), ...fields},
    }))
  }

  const fetchSlots = useCallback(
    (tab, date, spanish) => {
      // Region must be selected first for Thryv Starter onboarding calls
      if (isThryvStarterOnboarding && !selectedOnboardingRegion) {
        setTabField(tab, {loading: false, slots: [], selectedSlot: null})
        return
      }

      const callType = callTypes.find(t => t.key === tab) || callTypes[0]
      if (abortRef.current) abortRef.current.abort()
      abortRef.current = new AbortController()

      setTabField(tab, {loading: true, slots: [], selectedSlot: null})

      const dateStr = dayjs(date).format('YYYY-MM-DD')
      const country = proposalInfo.country || 'US'
      const langPref = spanish ? 'es' : 'en'
      const recurlyAddr = recurlyAccountInfo?.address || {}
      const addressParam = [
        recurlyAddr.street1 || proposalInfo.address,
        recurlyAddr.city || proposalInfo.city,
        recurlyAddr.region || proposalInfo.state,
      ]
      const codes = buildProductGroupCodes(selectedOption?.items ?? [])

      const url =
        `cc/order/available-times?appointmentType=${callType.appointmentType}` +
        `&country=${country}` +
        `&languagePreference=${langPref}` +
        `&date=${dateStr}&address=${encodeURIComponent(addressParam)}` +
        `&productGroupCodes=${codes.join(',')}` +
        `&thryvStarterOnboardingFlag=${isThryvStarterOnboarding}` +
        (isThryvStarterOnboarding && selectedOnboardingRegion
          ? `&region=${selectedOnboardingRegion}`
          : '')

      authClient(url, {signal: abortRef.current.signal})
        .then(data => {
          const tz = data?.timeZone?.timeZoneId || null
          const serviceId = data?.serviceIdTitle?.serviceId || null
          const raw = data?.availableTimes || []
          setTabField(tab, {
            timeZoneId: tz,
            serviceId: serviceId,
            loading: false,
            slots: raw.map(slot => ({
              ...slot,
              serviceId: slot.serviceId ?? serviceId,
              displayTime: formatSlotTime(slot.startTime, tz),
            })),
          })
        })
        .catch(err => {
          if (err?.name !== 'AbortError') {
            setTabField(tab, {loading: false, slots: []})
          }
        })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      proposalInfo.country,
      selectedOption,
      recurlyAccountInfo,
      isThryvStarterOnboarding,
      selectedOnboardingRegion,
    ],
  )
  // If the proposal already has a saved apptId (e.g. page reload / back navigation),
  // fetch the appointment details and pre-fill bookedSlots so the ScheduledBadge renders.
  useEffect(() => {
    if (proposalLoading || !proposalInfo.apptId) return
    const tabKey = callTypes[0].key
    // Avoid refetching if this session already booked/loaded the same appointment
    if (bookedSlots[tabKey]?.apptId === proposalInfo.apptId) return

    async function fetchExistingAppointment() {
      try {
        const apptDetail = await authClient(`proposals/${proposalId}/schedule`)
        const tz = apptDetail?.timeZone || null
        const startTime = apptDetail?.start_time || null
        setBookedSlots(prev => ({
          ...prev,
          [tabKey]: {
            apptId: proposalInfo.apptId,
            startTime,
            timeZoneId: tz,
            displayTime: startTime ? formatSlotTime(startTime, tz) : '',
            displayDate: startTime
              ? dayjs(startTime).format('MMM D, YYYY')
              : '',
            scheduledDate: startTime ? new Date(startTime) : null,
          },
        }))
      } catch {
        // silently fail — user can re-schedule manually
      }
    }
    fetchExistingAppointment()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposalLoading, proposalInfo.apptId])

  useEffect(() => {
    if (proposalLoading) return
    async function fetchScheduleOffsetDays() {
      try {
        const response = await authClient('cc/order/schedule-offset-days')
        if (response) setScheduleOffsetDetails(response)
      } catch {
        // non-fatal; calendarMinDate falls back to today
      }
    }
    fetchScheduleOffsetDays()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposalLoading])

  // Refetch when tab, spanish preference, or onboarding region changes;
  // use that tab's stored date. Guard against firing before proposalInfo is ready.
  useEffect(() => {
    if (proposalLoading) return
    const date = tabState[activeTab]?.selectedDate
    if (!date) return
    fetchSlots(activeTab, date, inSpanish)
    return () => abortRef.current?.abort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, inSpanish, proposalLoading, selectedOnboardingRegion])

  const SEO_PRODUCT_CODE = 'seobase-5-thryv-m2m'
  const DEFAULT_SEO_OFFSET_END_DAY = 30
  const DEFAULT_MAX_DAYS = 60
  // Use enrichedOption (proposalInfo.options) so alreadyIncluded is available —
  // items already on the account have alreadyIncluded: true set by setRecurlyAccountInfo.
  const hasNewSeoProduct = (enrichedOption?.items ?? [])
    .flatMap(item => [item, ...(item?.addons ?? [])])
    .some(
      item => item?.productCode === SEO_PRODUCT_CODE && !item?.alreadyIncluded,
    )
  const seoOffset = scheduleOffsetDetails.find(
    d => d.product_code === SEO_PRODUCT_CODE,
  )
  const calendarMinDate =
    hasNewSeoProduct && seoOffset?.offset_start_day
      ? dayjs().add(seoOffset.offset_start_day, 'day').toDate()
      : new Date()
  const calendarMaxDays = hasNewSeoProduct
    ? seoOffset?.offset_end_day ?? DEFAULT_SEO_OFFSET_END_DAY
    : DEFAULT_MAX_DAYS
  const calendarMaxDate = dayjs().add(calendarMaxDays, 'day').toDate()

  // A date picked before the cap applied (e.g. new SEO added later) must not stay selected.
  useEffect(() => {
    if (!selectedDate) return
    if (
      dayjs(selectedDate).isAfter(dayjs().add(calendarMaxDays, 'day'), 'day')
    ) {
      setTabField(activeTab, {selectedDate: null, selectedSlot: null})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calendarMaxDays, selectedDate, activeTab])

  async function handleScheduleCall() {
    if (!selectedSlot || scheduling) return
    setScheduling(true)
    setScheduleError(null)

    const firstName = proposalInfo.clientFirstName
    const lastName = proposalInfo.clientLastName
    const country = proposalInfo.country || 'US'

    const callKey = 'onboarding'
    const payload = {
      firstName,
      lastName,
      email: proposalInfo.clientEmail || '',
      phone: proposalInfo.clientPhone || '',
      company: proposalInfo.businessName || '',
      isKeapClientPortalReq: true,
      [callKey]: {
        timeZone: timeZoneId,
        serviceId,
        staffId: selectedSlot.staffId,
        startTime: selectedSlot.startTime,
        thryvBookingDate: selectedSlot.startDateTime,
        country,
        isAus: country === 'AU',
        isNZ: country === 'NZ',
        isApptHold: true,
        languagePreference: inSpanish ? 'es' : 'en',
      },
    }

    try {
      const result = await authClient(activeCallType.endpoint, {data: payload})
      setBookedSlots(prev => ({
        ...prev,
        [activeTab]: {
          ...selectedSlot,
          apptId: result?.apptId,
          timeZoneId,
          displayDate: dayjs(selectedDate).format('MMM D, YYYY'),
          scheduledDate: selectedDate,
        },
      }))
      setTabField(activeTab, {selectedSlot: null})
    } catch (err) {
      setScheduleError(err?.message || 'Failed to schedule. Please try again.')
    } finally {
      setScheduling(false)
    }
  }

  async function handleClearSchedule() {
    const apptId = bookedSlots[callTypes[0].key]?.apptId
    await authClient(`plans/onboarding`, {
      data: {
        demoApptId: apptId,
        type: 'onboarding',
        country: proposalInfo.country,
      },
      method: 'DELETE',
    })

    await authClient(`proposals/${proposalId}`, {
      data: {apptId: null},
      method: 'PUT',
    })
    dispatch(updateProposalInfo({apptId: null}))
    setBookedSlots(prev => {
      const next = {...prev}
      delete next[activeTab]
      return next
    })
    setTabField(activeTab, {selectedSlot: null})
    setScheduleError(null)
  }

  function handleDateChange(date) {
    setTabField(activeTab, {selectedDate: date, selectedSlot: null})
    fetchSlots(activeTab, date, inSpanish)
    setScheduleError(null)
  }

  async function handleContinue() {
    if (!allScheduled || continuing) return
    const apptId = bookedSlots[callTypes[0].key]?.apptId
    const alreadySaved = apptId && apptId === proposalInfo.apptId
    if (apptId && !alreadySaved) {
      setContinuing(true)
      try {
        dispatch(updateProposalInfo({apptId}))
        await authClient(`proposals/${proposalId}`, {
          data: {apptId},
          method: 'PUT',
        })
      } catch (err) {
        console.error('Failed to save apptId to proposal:', err)
      } finally {
        setContinuing(false)
      }
    }
    navigate(`/proposal-view/${proposalId}/client-payment`)
  }

  const allScheduled = callTypes.every(t => bookedSlots[t.key])
  const currentTabBooked = !!bookedSlots[activeTab]

  // The scheduling time zone comes from the available-times response; once an
  // appointment exists, prefer the zone it was actually booked in.
  const bookedSlot = bookedSlots[activeTab]
  const activeTimeZoneId = bookedSlot?.timeZoneId || timeZoneId
  const timeZoneLabel = formatTimeZoneLabel(
    activeTimeZoneId,
    bookedSlot?.startTime || selectedSlot?.startTime || selectedDate,
  )

  return (
    <PageWrap>
      <Container>
        <NavBar>
          <NavLeft>
            <img
              src={PTLogoWhite}
              alt="Thryv"
              style={{height: 30, objectFit: 'contain'}}
            />
            <span />
            <img
              src={PTKeapWhite}
              alt="Keap"
              style={{height: 30, objectFit: 'contain'}}
            />
          </NavLeft>
          {proposalInfo.businessName && (
            <NavRight>
              <NavForLabel>Proposal for</NavForLabel>
              <NavBusinessName>{proposalInfo.businessName}</NavBusinessName>
            </NavRight>
          )}
        </NavBar>

        <GradientSection style={{flex: 1}}>
          <HeroArea>
            <PageTitle>Schedule Your Appointment</PageTitle>
            <PageSubtitle>
              Your selected plan includes a service that requires an{' '}
              <strong>Onboarding</strong> call.
            </PageSubtitle>
          </HeroArea>
          <CardsSection>
            <CardOuter>
              <ScheduleCard>
                <CardBody>
                  <SpanishRow>
                    <SpanishLabel>
                      Would you like to schedule this call in Spanish?
                    </SpanishLabel>
                    <ToggleGroup>
                      <ToggleBtn
                        type="button"
                        active={inSpanish}
                        onClick={() => setInSpanish(true)}
                      >
                        Yes
                      </ToggleBtn>
                      <ToggleBtn
                        type="button"
                        active={!inSpanish}
                        onClick={() => setInSpanish(false)}
                      >
                        No
                      </ToggleBtn>
                    </ToggleGroup>
                  </SpanishRow>

                  {isThryvStarterOnboarding && (
                    <RegionRow>
                      <RegionLabel htmlFor="onboarding-region">
                        Select Region
                      </RegionLabel>
                      <RegionSelect
                        id="onboarding-region"
                        value={selectedOnboardingRegion}
                        onChange={e =>
                          setSelectedOnboardingRegion(e.target.value)
                        }
                      >
                        <option value="">-- Select a region --</option>
                        {ONBOARDING_REGION_OPTIONS.map(opt => (
                          <option key={opt.value} value={opt.value}>
                            {opt.name}
                          </option>
                        ))}
                      </RegionSelect>
                    </RegionRow>
                  )}

                  <ScheduleColumns>
                    <ScheduleColumn>
                      <ColumnLabel>
                        <PTCalendarDaySVG
                          width="14"
                          height="14"
                          style={{color: '#374151'}}
                        />
                        Select a Date
                        {timeZoneLabel && (
                          <TimeZoneTag title={activeTimeZoneId}>
                            {timeZoneLabel}
                          </TimeZoneTag>
                        )}
                      </ColumnLabel>
                      <CalendarWrap>
                        <Calendar
                          value={selectedDate}
                          onChange={handleDateChange}
                          minDate={calendarMinDate}
                          maxDate={calendarMaxDate}
                          locale="en-US"
                        />
                      </CalendarWrap>
                    </ScheduleColumn>

                    <ScheduleColumn>
                      <ColumnLabel>
                        <PTClockSVG
                          width="14"
                          height="14"
                          style={{color: '#374151'}}
                        />
                        Available Time slot
                        {timeZoneLabel && (
                          <TimeZoneTag title={activeTimeZoneId}>
                            All times in {timeZoneLabel}
                          </TimeZoneTag>
                        )}
                      </ColumnLabel>

                      {currentTabBooked ? (
                        <ScheduledBadge>
                          &#10003; Scheduled for{' '}
                          {bookedSlots[activeTab].displayDate} at{' '}
                          {bookedSlots[activeTab].displayTime}
                          {timeZoneLabel ? ` ${timeZoneLabel}` : ''}
                        </ScheduledBadge>
                      ) : (
                        <SlotGrid>
                          {!selectedDate ||
                          (isThryvStarterOnboarding &&
                            !selectedOnboardingRegion) ? (
                            <SlotsEmpty>
                              {isThryvStarterOnboarding &&
                              !selectedOnboardingRegion
                                ? 'Select a region and date to see available time slots.'
                                : 'Select a date to see available time slots.'}
                            </SlotsEmpty>
                          ) : loadingSlots ? (
                            <SlotsLoading>
                              Loading available times…
                            </SlotsLoading>
                          ) : slots.length === 0 ? (
                            <SlotsEmpty>
                              No times available for this date.
                            </SlotsEmpty>
                          ) : (
                            slots.map((slot, i) => (
                              <SlotBtn
                                key={slot.startTime || i}
                                type="button"
                                selected={
                                  selectedSlot?.startTime === slot.startTime
                                }
                                onClick={() =>
                                  setTabField(activeTab, {selectedSlot: slot})
                                }
                              >
                                {slot.displayTime}
                              </SlotBtn>
                            ))
                          )}
                        </SlotGrid>
                      )}

                      {scheduleError && (
                        <p
                          style={{
                            color: '#ef4444',
                            fontSize: 13,
                            margin: '10px 0 0',
                          }}
                        >
                          {scheduleError}
                        </p>
                      )}
                    </ScheduleColumn>
                  </ScheduleColumns>
                  <ActionBar>
                    <ActionLeft>
                      <Button
                        variant="secondary-gray"
                        type="button"
                        onClick={() =>
                          navigate(
                            `/proposal-view/${proposalId}/account-information`,
                          )
                        }
                        leftIcon={<PTArrowLeftSVG width="14" height="14" />}
                        size="md"
                        style={{borderRadius: '80px 0 0 80px'}}
                      >
                        Back
                      </Button>
                      <Button
                        variant="secondary-gray"
                        type="button"
                        onClick={() => navigate(`/proposal-view/${proposalId}`)}
                        size="md"
                        style={{borderRadius: '0 80px 80px 0'}}
                      >
                        Cancel
                      </Button>
                    </ActionLeft>

                    <ActionRight>
                      {currentTabBooked ? (
                        <Button
                          variant="solid-gradiant"
                          type="button"
                          onClick={handleClearSchedule}
                          leftIcon={
                            <PTCalendarCallSVG
                              width="14"
                              height="14"
                              style={{color: '#ffffff'}}
                            />
                          }
                          size="md"
                        >
                          Reschedule Call
                        </Button>
                      ) : (
                        <Button
                          variant="solid-gradiant"
                          type="button"
                          isLoading={scheduling}
                          disabled={!selectedSlot || scheduling}
                          onClick={handleScheduleCall}
                          leftIcon={
                            !scheduling && (
                              <PTCalendarCallSVG
                                width="14"
                                height="14"
                                style={{color: '#ffffff'}}
                              />
                            )
                          }
                          size="md"
                        >
                          Schedule Call
                        </Button>
                      )}
                      <Button
                        variant={'solid-gradiant'}
                        size="md"
                        type="button"
                        isLoading={continuing}
                        disabled={!allScheduled || continuing}
                        onClick={handleContinue}
                        style={{
                          borderRadius: '100px',
                          letterSpacing: '0.6px',
                        }}
                      >
                        Continue
                        {!continuing && (
                          <PTArrowRightSVG width="14" height="14" />
                        )}
                      </Button>
                    </ActionRight>
                  </ActionBar>
                </CardBody>
              </ScheduleCard>
            </CardOuter>
          </CardsSection>

          <PageFooter>
            <FooterText>Copyright &copy; 2026 Thryv, Inc.</FooterText>
          </PageFooter>
        </GradientSection>
      </Container>
    </PageWrap>
  )
}

export {ClientProposalSchedule}
